import React, { useMemo, useState } from "react";
import { FiUser, FiMail, FiPhone, FiBriefcase, FiUsers, FiLock } from "react-icons/fi";

import SectionHeader from "../components/ui/SectionHeader";
import StatCard from "../components/ui/StatCard";
import StatusBadge from "../components/ui/StatusBadge";
import { useCreateEmployeeMutation, useDeleteEmployeeMutation, useGetEmployeesQuery, useUpdateEmployeeMutation } from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";

const emptyForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  role: "manager",
  department: "",
  status: "active",
  workload_capacity: 100,
  password: "",
};

const EmployeesPage = () => {
  const { data } = useGetEmployeesQuery();
  const [createEmployee] = useCreateEmployeeMutation();
  const [updateEmployee] = useUpdateEmployeeMutation();
  const [deleteEmployee] = useDeleteEmployeeMutation();
  const [editingId, setEditingId] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const employees = useMemo(() => data?.data || [], [data]);

  const summary = useMemo(() => {
    const activeEmployees = employees.filter((employee) => employee.status === "active");
    const averageProductivity = employees.length
      ? employees.reduce((sum, employee) => sum + Number(employee.productivity_score || 0), 0) / employees.length
      : 0;
    const activeOrders = employees.reduce((sum, employee) => sum + Number(employee.active_orders || 0), 0);
    const completedTasks = employees.reduce((sum, employee) => sum + Number(employee.completed_tasks || 0), 0);

    return {
      total: employees.length,
      active: activeEmployees.length,
      averageProductivity,
      activeOrders,
      completedTasks,
    };
  }, [employees]);

  const topPerformer = useMemo(() => {
    if (!employees.length) {
      return null;
    }

    return [...employees].sort(
      (left, right) => Number(right.productivity_score || 0) - Number(left.productivity_score || 0)
    )[0];
  }, [employees]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setIsSubmitting(true);
    try {
      if (editingId) {
        await updateEmployee({ id: editingId, ...form }).unwrap();
      } else {
        await createEmployee(form).unwrap();
      }
      setEditingId(null);
      setForm(emptyForm);
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Unable to save employee."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditing = (employee) => {
    setEditingId(employee.id);
    setForm({
      ...employee,
      password: "",
    });
    setSubmitError("");
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSubmitError("");
  };

  return (
    <div className="dashboard-page">
      <SectionHeader
        eyebrow="Employees"
        title="A more beautiful, clearer team workspace for roles, performance, and workload."
        description="Track who is active, who is overloaded, and who is driving output across production."
      />

      <div className="stats-grid">
        <StatCard label="Team members" value={summary.total} helper={`${summary.active} active right now`} />
        <StatCard label="Avg productivity" value={`${summary.averageProductivity.toFixed(1)}%`} tone="success" helper="Across the current employee list" />
        <StatCard label="Active orders" value={summary.activeOrders} tone="warning" helper="Assigned across the team" />
        <StatCard label="Completed tasks" value={summary.completedTasks} helper="Operational output snapshot" />
      </div>

      <div className="dashboard-grid dashboard-grid--wide employee-layout">
        <section className="employee-collection">
          {topPerformer ? (
            <article className="panel employee-hero-card">
              <div className="employee-hero-card__badge">Top performer</div>
              <div className="employee-hero-card__row">
                <div>
                  <h3>{topPerformer.first_name} {topPerformer.last_name}</h3>
                  <p>{topPerformer.role} in {topPerformer.department || "General operations"}</p>
                </div>
                <StatusBadge value={topPerformer.status} />
              </div>
              <div className="employee-hero-metrics">
                <div>
                  <strong>{topPerformer.productivity_score || 0}%</strong>
                  <span>Productivity</span>
                </div>
                <div>
                  <strong>{topPerformer.active_orders || 0}</strong>
                  <span>Active orders</span>
                </div>
                <div>
                  <strong>{topPerformer.completed_tasks || 0}</strong>
                  <span>Tasks done</span>
                </div>
              </div>
            </article>
          ) : null}

          <div className="employee-grid">
            {employees.map((employee) => (
              <article key={employee.id} className="panel employee-card">
                <div className="employee-card__top">
                  <div className="employee-avatar">
                    {employee.first_name?.[0]}{employee.last_name?.[0]}
                  </div>
                  <div className="employee-card__identity">
                    <strong>{employee.first_name} {employee.last_name}</strong>
                    <span>{employee.role}</span>
                  </div>
                  <StatusBadge value={employee.status} />
                </div>

                <div className="employee-card__body">
                  <div className="employee-card__meta">
                    <span>{employee.department || "No department"}</span>
                    <span>{employee.email}</span>
                    <span>{employee.phone || "No phone set"}</span>
                  </div>

                  <div className="employee-performance">
                    <div className="employee-performance__item">
                      <small>Productivity</small>
                      <strong>{employee.productivity_score || 0}%</strong>
                    </div>
                    <div className="employee-performance__item">
                      <small>Active orders</small>
                      <strong>{employee.active_orders || 0}</strong>
                    </div>
                    <div className="employee-performance__item">
                      <small>Tasks done</small>
                      <strong>{employee.completed_tasks || 0}</strong>
                    </div>
                  </div>

                  <div className="employee-capacity">
                    <div className="employee-capacity__label">
                      <span>Workload capacity</span>
                      <strong>{employee.workload_capacity || 0}%</strong>
                    </div>
                    <div className="employee-capacity__track">
                      <div
                        className="employee-capacity__fill"
                        style={{ width: `${Math.max(0, Math.min(100, Number(employee.workload_capacity || 0)))}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="employee-card__actions">
                  <button className="table-action" type="button" onClick={() => startEditing(employee)}>
                    Edit
                  </button>
                  <button className="table-action table-action--danger" type="button" onClick={() => deleteEmployee(employee.id)}>
                    Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <form className="panel form-panel employee-form-panel form-modern" onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-section__header">
              <h3 className="form-section__title">{editingId ? "Edit Employee" : "Add Team Member"}</h3>
              <p className="form-section__subtitle">{editingId ? "Update employee profile and settings" : "Create a new employee account"}</p>
            </div>

            {submitError && (
              <div className="form-alert form-alert--danger">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{submitError}</span>
              </div>
            )}

            <div className="form-grid form-grid--2">
              <div className="form-group">
                <label className="form-label form-label--required">
                  <FiUser /> First Name
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiUser /></span>
                  <input 
                    className="form-input"
                    value={form.first_name} 
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })} 
                    placeholder="Enter first name" 
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label form-label--required">
                  <FiUser /> Last Name
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiUser /></span>
                  <input 
                    className="form-input"
                    value={form.last_name} 
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })} 
                    placeholder="Enter last name" 
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label form-label--required">
                  <FiMail /> Email Address
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiMail /></span>
                  <input 
                    className="form-input"
                    type="email"
                    value={form.email} 
                    onChange={(e) => setForm({ ...form, email: e.target.value })} 
                    placeholder="employee@melona.com" 
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FiPhone /> Phone Number
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiPhone /></span>
                  <input 
                    className="form-input"
                    type="tel"
                    value={form.phone} 
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                    placeholder="+251 9XX XXX XXX" 
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label form-label--required">
                  <FiBriefcase /> Role
                </label>
                <select className="form-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="designer">Designer</option>
                  <option value="printer">Printer</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FiUsers /> Department
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiUsers /></span>
                  <input 
                    className="form-input"
                    value={form.department} 
                    onChange={(e) => setForm({ ...form, department: e.target.value })} 
                    placeholder="e.g. Production, Design" 
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Status
                </label>
                <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Workload Capacity (%)
                  <span className="form-label__hint">Current work capacity</span>
                </label>
                <input 
                  className="form-input"
                  type="number" 
                  min="0"
                  max="100"
                  value={form.workload_capacity} 
                  onChange={(e) => setForm({ ...form, workload_capacity: Number(e.target.value) })} 
                  placeholder="100" 
                />
              </div>

              <div className="form-group form-group--full">
                <label className="form-label">
                  <FiLock /> Password
                  <span className="form-label__hint">{editingId ? "(Leave empty to keep current password)" : "(Required for new employee)"}</span>
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiLock /></span>
                  <input 
                    className="form-input"
                    type="password"
                    value={form.password} 
                    onChange={(e) => setForm({ ...form, password: e.target.value })} 
                    placeholder={editingId ? "Enter new password to change" : "Enter secure password"} 
                    required={!editingId}
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button className="btn-secondary" type="button" onClick={resetForm}>
                {editingId ? "Cancel" : "Reset"}
              </button>
              <button 
                className={`btn-primary ${isSubmitting ? 'btn-primary--loading' : ''}`}
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : editingId ? "Update Employee" : "Add Employee"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeesPage;
